import DeliveryIcon from "@NA/Icons/DeliveryIcon";
import StoreIcon from "@NA/Icons/StoreIcon";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { ProductModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";
import { getMaxProductHeight } from "@NA/Utils/Utility";
import ADILBStorePickup from "@NA/Components/ADILBStorePickup";

interface OwnProps {
    product: ProductModel;
    freeShippingAmount: number;
    showAuthorizationRequired: string;
    extendedStyles?: ADIProductDeliveryInfoStyles;
    pricing?: ProductPriceDto;
}

type Props = OwnProps;

export interface ADIProductDeliveryInfoStyles {
    listDisplay?: InjectableCss;
    listDisplayWrapper?: InjectableCss;
    free?: TypographyProps;
    notFree?: TypographyProps;
}

export const adiProductDeliveryInfoStyles: ADIProductDeliveryInfoStyles = {
    listDisplay: {
        css: css`
            display: flex;
            align-items: flex-start;
            margin-top: 15px;
            font-weight: bold;
            &[data-test-selector="freeStorePickup"] {
                margin-top: 10px;
            }
            svg {
                margin-right: 7px;
                min-width: 16px;
            }
            span {
                font-size: 12px;

                color: ${ADIColors.darkBlue1};
                display: block;
            }
        `,
    },
    listDisplayWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css``,
                        css``,
                        css`
                            margin-bottom: 0px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    free: {
        css: css`
            color: ${ADIColors.lightGreen1} !important;
        `,
    },
    notFree: {
        css: css`
            color: ${ADIColors.orange1} !important;
        `,
    },
};

const ADIProductDeliveryInfo: FC<Props> = ({
    product,
    freeShippingAmount,
    showAuthorizationRequired,
    extendedStyles,
    pricing,
}) => {
    const [styles] = React.useState(() => mergeToNew(adiProductDeliveryInfoStyles, extendedStyles));

    const [minStoreHeight, setMinStoreHeight] = useState(15);
    const [minDeliveryHeight, setMinDeliveryHeight] = useState(15);
    useEffect(() => {
        const maxStoreHeight = getMaxProductHeight('[data-test-selector="freeStorePickup"]');
        const maxDeliveryHeight = getMaxProductHeight('[data-test-selector="delivery"]');
        setMinStoreHeight(maxStoreHeight);
        setMinDeliveryHeight(maxDeliveryHeight);
    }, []);

    const isLbProduct = product?.properties?.logicBroker?.toLowerCase() === "yes";

    const isPRRegion = typeof window !== "undefined" && window.location.hostname.endsWith(".pr");

    if (showAuthorizationRequired !== "true") {
        return (
            <StyledWrapper {...styles.listDisplayWrapper}>
                <ADILBStorePickup isLbProduct={isLbProduct} />
                {!isPRRegion && (
                    <StyledWrapper
                        {...styles.listDisplay}
                        data-test-selector="delivery"
                        style={{ minHeight: minDeliveryHeight + "px" }}
                    >
                        <DeliveryIcon />
                        <StyledWrapper>
                            <Typography>{translate("Delivery")}</Typography>
                            <Typography
                                {...(pricing && pricing?.unitListPrice >= freeShippingAmount
                                    ? styles.free
                                    : styles.notFree)}
                            >
                                {(
                                    product.unitListPrice == undefined
                                        ? pricing && pricing?.unitListPrice >= freeShippingAmount
                                        : product.unitListPrice >= freeShippingAmount
                                )
                                    ? translate("Free")
                                    : translate(`Free for orders $${freeShippingAmount} & above`)}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    } else {
        return null;
    }
};

export default ADIProductDeliveryInfo;
